"use client";
import {
  Box,
  CloseButton,
  Flex,
  Icon,
  IconButton,
  IconButtonProps,
  Portal,
  SlideDirection,
  SlideFade,
  Text,
  ToastOptions,
} from "@chakra-ui/react";
import { useEffect, useMemo } from "react";
import { IconType } from "react-icons";
import { LuWifiOff } from "react-icons/lu";
import {
  SnackbarActionType,
  useSnackBarContext,
} from "../../context/SnackbarContext";

export interface SnackbarProps {
  position?: ToastOptions["position"];
  open?: boolean;
  icon?: IconType;
  iconProps?: IconButtonProps;
  text?: string;
  actionButton?: React.ReactNode;
}

function getBoxPosition(position: ToastOptions["position"]) {
  const positionParam: {
    top?: number | string;
    bottom?: number | string;
    right?: number | string;
    left?: number | string;
    transform?: string;
  } = {};

  switch (position) {
    case "top":
      positionParam["top"] = 10;
      positionParam["left"] = "50%";
      positionParam["transform"] = "translateX(-50%)";
      break;
    case "top-left":
      positionParam["top"] = 10;
      positionParam["left"] = 10;
      break;
    case "top-right":
      positionParam["top"] = 10;
      positionParam["right"] = 10;
      break;
    case "bottom":
      positionParam["bottom"] = 10;
      positionParam["left"] = "50%";
      positionParam["transform"] = "translateX(-50%)";
      break;
    case "bottom-left":
      positionParam["bottom"] = 10;
      positionParam["left"] = 10;
      break;
    case "bottom-right":
      positionParam["bottom"] = 10;
      positionParam["right"] = 10;
      break;
    default:
      positionParam["bottom"] = 10;
  }

  return positionParam;
}

function getSlideDirection(position: ToastOptions["position"]): SlideDirection {
  if (position.includes("top")) {
    return "top";
  } else if (position.includes("right")) {
    return "right";
  } else if (position.includes("left")) {
    return "left";
  } else {
    return "bottom";
  }
}

export function Snackbar({
  open = false,
  position = "top-right",
  icon = LuWifiOff,
  iconProps,
  text,
  actionButton,
}: SnackbarProps) {
  const dispatch = useSnackBarContext()[1];
  const { boxPosition } = useMemo(() => {
    return {
      boxPosition: position ? getBoxPosition(position) : {},
      slideDirection: getSlideDirection(position),
    };
  }, [position]);

  useEffect(() => {
    return () => {
      dispatch({
        type: SnackbarActionType.RESET_SNACKBAR,
        payload: {},
      });
    };
  }, [dispatch]);

  return text ? (
    <Portal appendToParentPortal>
      <SlideFade in={open}>
        <Flex
          sx={{
            ...boxPosition,
            minWidth: "20rem",
            maxWidth: "30rem",
            position: "fixed",
            alignItems: "center",
            zIndex: 2,
            shadow: "md",
            p: "1rem",
            borderRadius: "md",
            background: "gray.50",
          }}
        >
          {icon && (
            <IconButton
              aria-label="snackbar-icon"
              rounded="full"
              size="md"
              sx={{
                mr: "1rem",
                fontSize: "1.25rem",
              }}
              {...iconProps}
              icon={<Icon as={icon} />}
            />
          )}
          <Box flex={1}>
            <Text fontSize="1rem">{text}</Text>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: "1rem",
            }}
          >
            {actionButton}
            <CloseButton
              onClick={() => {
                dispatch({
                  type: SnackbarActionType.CLOSE_SNACKBAR,
                  payload: {},
                });
              }}
            />
          </Box>
        </Flex>
      </SlideFade>
    </Portal>
  ) : null;
}
